/**
 * Fase 6 — Leaflet-basert lokasjonsvelger for kurs-veiviser.
 *
 * Bruker react-leaflet + OpenStreetMap-tiles (gratis, 0 API-nøkler).
 * Adressesøk via Nominatim (OSM's gratis geocoding — rate limit 1 req/s + User-Agent-krav).
 *
 * Props:
 *   - lat/lng: valgfri start-koordinat (hvis NULL, kart sentreres på Norge)
 *   - address: full adressetekst
 *   - onChange: kalles når bruker flytter nål eller velger søkeresultat
 */
import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import type { LatLngExpression, LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, Loader2, Target } from 'lucide-react';

// Fix for default marker icon-path-problem i bundlers
// (react-leaflet/leaflet laster ikoner fra samme domene, men Vite re-bundler stier)
const DefaultIcon = L.icon({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
}

export interface LocationPickerProps {
  lat: number | null;
  lng: number | null;
  address: string;
  onChange: (next: { lat: number; lng: number; address: string }) => void;
}

// Default senter: midt i Norge
const DEFAULT_CENTER: LatLngExpression = [63.4305, 10.3951]; // Trondheim
const DEFAULT_ZOOM = 5;
const SELECTED_ZOOM = 15;

export default function LocationPicker({ lat, lng, address, onChange }: LocationPickerProps) {
  const [query, setQuery] = useState(address);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Oppdater søkefeltet hvis adresse endres utenfra (f.eks. ved mount)
  useEffect(() => {
    setQuery(address);
  }, [address]);

  // Debounced Nominatim-søk
  const search = async (q: string) => {
    if (!q || q.trim().length < 3) {
      setResults([]);
      setShowResults(false);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    try {
      const url = new URL('https://nominatim.openstreetmap.org/search');
      url.searchParams.set('q', q);
      url.searchParams.set('format', 'json');
      url.searchParams.set('addressdetails', '1');
      url.searchParams.set('limit', '5');
      url.searchParams.set('countrycodes', 'no'); // Prioriter Norge
      const res = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          // Nominatim krever identifiserbar User-Agent
          // (browser setter denne automatisk, men vi legger til Accept-Language)
          'Accept-Language': 'nb,en',
        },
      });
      if (!res.ok) throw new Error(`Nominatim ${res.status}`);
      const data: NominatimResult[] = await res.json();
      setResults(data);
      setShowResults(true);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.warn('[LocationPicker] Nominatim-søk feilet:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounce søk 400ms etter input
  useEffect(() => {
    if (query === address) return;
    const t = setTimeout(() => search(query), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const selectResult = (r: NominatimResult) => {
    const nextLat = parseFloat(r.lat);
    const nextLng = parseFloat(r.lon);
    onChange({ lat: nextLat, lng: nextLng, address: r.display_name });
    setQuery(r.display_name);
    setShowResults(false);
  };

  // Reverse geocoding når bruker klikker/flytter nål
  const reverseGeocode = async (nextLat: number, nextLng: number) => {
    setReverseLoading(true);
    try {
      const url = new URL('https://nominatim.openstreetmap.org/reverse');
      url.searchParams.set('lat', nextLat.toString());
      url.searchParams.set('lon', nextLng.toString());
      url.searchParams.set('format', 'json');
      url.searchParams.set('zoom', '18');
      const res = await fetch(url.toString(), { headers: { 'Accept-Language': 'nb,en' } });
      if (!res.ok) throw new Error(`Nominatim-reverse ${res.status}`);
      const data = await res.json();
      const nextAddress = data.display_name || `${nextLat.toFixed(5)}, ${nextLng.toFixed(5)}`;
      onChange({ lat: nextLat, lng: nextLng, address: nextAddress });
      setQuery(nextAddress);
    } catch (err: unknown) {
      // Fallback: lagre kun koordinater uten adresse-oppslag
      console.warn('[LocationPicker] Reverse-geocoding feilet:', err);
      const fallback = `${nextLat.toFixed(5)}, ${nextLng.toFixed(5)}`;
      onChange({ lat: nextLat, lng: nextLng, address: fallback });
      setQuery(fallback);
    } finally {
      setReverseLoading(false);
    }
  };

  const hasPin = typeof lat === 'number' && typeof lng === 'number';
  const center: LatLngExpression = hasPin ? [lat!, lng!] : DEFAULT_CENTER;
  const zoom = hasPin ? SELECTED_ZOOM : DEFAULT_ZOOM;

  return (
    <div className="space-y-3">
      {/* Søkefelt */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-kf-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => results.length > 0 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder="Søk etter adresse (f.eks. Storgata 10, Oslo)"
              className="w-full pl-10 pr-10 py-2 text-sm border border-kf-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
            />
            {(searching || reverseLoading) && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-kf-blue-600 animate-spin" />
            )}
          </div>
        </div>

        {/* Søkeresultat-dropdown */}
        {showResults && results.length > 0 && (
          <div className="absolute z-[1000] mt-1 w-full bg-white border border-kf-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.place_id}
                type="button"
                onMouseDown={() => selectResult(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-kf-blue-50 border-b border-kf-gray-100 last:border-b-0"
              >
                {r.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Kart */}
      <div className="rounded-lg overflow-hidden border border-kf-gray-300" style={{ height: '320px' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-bidragsytere'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEvents onPick={reverseGeocode} />
          {hasPin && (
            <Marker
              position={[lat!, lng!]}
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target as L.Marker;
                  const pos = marker.getLatLng();
                  reverseGeocode(pos.lat, pos.lng);
                },
              }}
            />
          )}
          <CenterOnPin lat={lat} lng={lng} />
        </MapContainer>
      </div>

      {/* Info-rad */}
      <div className="flex items-start gap-2 text-xs text-kf-gray-600">
        <Target className="w-3.5 h-3.5 shrink-0 mt-0.5 text-kf-blue-600" />
        <div>
          <strong>Flytt nålen</strong> ved å dra den, eller <strong>klikk på kartet</strong> for å plassere/flytte nålen.
          Nålen bør være ved selve kurs-inngangen (noen ganger avviker inngangen fra postadressens hovedinngang).
          {hasPin && (
            <span className="ml-1 text-kf-gray-500">
              Koordinater: {lat!.toFixed(5)}, {lng!.toFixed(5)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Håndter klikk på kartet
function MapEvents({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e: LeafletMouseEvent) => {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Sentrer kartet på pin når den endres (f.eks. etter søkeresultat)
function CenterOnPin({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === 'number' && typeof lng === 'number') {
      map.flyTo([lat, lng], SELECTED_ZOOM, { duration: 0.8 });
    }
  }, [lat, lng, map]);
  return null;
}
